"use client"

import root from "react-shadow"
import { motion, Variants } from "framer-motion"
import { Clock, HelpCircle, Facebook, Instagram, ArrowRight } from "lucide-react"

import { Button } from "./components/ui/button"
import { Card, CardContent } from "./components/ui/card"
import ProductCarousel from "./components/product-carousel"
import { useEffect, useRef } from "react"

import vikikaLogo from "../../../assets/img/vikika-logo.png"
import testAutoestimaImage from "../../../assets/img/testautoestima.jpg"
import testAutoexigenciaImage from "../../../assets/img/testautoexigencia.jpg"
import testEntrenamientoImage from "../../../assets/img/testentrenamiento.jpg"
import testHabitosImage from "../../../assets/img/testhabitos.jpg"

// Variantes de animación
const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
}

const fadeInLeft: Variants = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
}

const fadeInRight: Variants = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
}

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1
    }
  }
}

const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, ease: "easeOut" }
  }
}

const bounceIn: Variants = {
  hidden: { opacity: 0, scale: 0.3 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: "easeOut",
      type: "spring",
      stiffness: 100
    }
  }
}

export default function TestsView() {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const attachShadowAndLoadCSS = async () => {
      if (!containerRef.current) return

      const host = containerRef.current
      const shadow = host.shadowRoot

      try {
        const res = await fetch("./tests.css")
        const css = await res.text()
        const style = document.createElement("style")
        style.textContent = css
        shadow?.appendChild(style)
      } catch (error) {
        console.error("Error loading CSS:", error)
      }
    }

    attachShadowAndLoadCSS()
  }, [])

  return (
    <root.div ref={containerRef}>
      <div id="main" className="min-h-screen bg-white">
        {/* Header */}
        <motion.header
          className="text-center pt-12 pb-8"
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <div className="flex justify-center">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <img
                src={vikikaLogo}
                alt="Vikika Team Logo"
                width={400}
                height={80}
                className="h-16 w-auto"
              />
            </motion.div>
          </div>
        </motion.header>

        {/* Main Content */}
        <main className="max-w-6xl mx-auto px-6">
          {/* Hero Section */}
          <motion.section
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.h2
              className="text-3xl font-semibold mb-6"
              style={{ color: "#314037" }}
              variants={fadeInUp}
            >
              Tu punto de partida para el cambio empieza aquí
            </motion.h2>
            <motion.p
              className="max-w-4xl mx-auto leading-relaxed text-lg mb-4"
              style={{ color: "#68736e" }}
              variants={fadeInUp}
            >
              Sientes que algo no funciona: tu relación con la comida se complica, te exiges
              demasiado, tu autoestima flaquea o tu entrenamiento no da resultados. Estos 4 tests te
              ayudarán a identificar con precisión qué está bloqueando tu progreso. Al completarlos,
              recibirás una orientación clara basada en tus respuestas, para que puedas dar el
              siguiente paso con más seguridad.
            </motion.p>
          </motion.section>

          {/* Test Cards */}
          <motion.section
            className="grid md:grid-cols-2 gap-8 mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            {/* Test 1 - Personas Altamente Sensibles */}
            <motion.div variants={fadeInLeft}>
              <Card
                className="overflow-hidden shadow-lg border-0 h-full"
                style={{ backgroundColor: "#f2f2f2" }}
              >
                <motion.div
                  className="relative h-64"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={testAutoestimaImage}
                    alt="Mujer reflexionando frente al espejo - Test de Autoestima"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                <CardContent className="p-6">
                  <motion.h3
                    className="text-xl font-semibold mb-4"
                    style={{ color: "#314037" }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    Test de autoestima
                  </motion.h3>

                  {/* Test Info */}
                  <motion.div
                    className="flex items-center justify-center gap-6 mb-4 rounded-lg py-3"
                    style={{ backgroundColor: "#d7ceca" }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="flex items-center gap-1" style={{ color: "#68736e" }}>
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">5 min</span>
                    </div>
                    <div className="flex items-center gap-1" style={{ color: "#68736e" }}>
                      <HelpCircle className="w-4 h-4" />
                      <span className="text-sm">15 preguntas</span>
                    </div>
                  </motion.div>

                  <motion.p
                    className="text-sm mb-4 leading-relaxed"
                    style={{ color: "#68736e" }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    ¿Te paralizan las dudas sobre ti mism@? ¿Sientes que no te valoras como mereces?
                    Este test te ayudará a descubrir qué bloquea tu autoestima y cómo empezar a
                    transformar tu diálogo interno. Atrévete a conocerte mejor y dar el primer paso
                    hacia una versión más segura y auténtica de ti.
                  </motion.p>

                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <Button
                      className="w-full text-white py-3 border-0 hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: "#314037" }}
                      onClick={() => window.open("https://form.typeform.com/to/VQWhr8g8", "_blank")}
                    >
                      TEST DE AUTOESTIMA
                    </Button>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Test 2 - Ansiedad */}
            <motion.div variants={fadeInUp}>
              <Card
                className="overflow-hidden shadow-lg border-0 h-full"
                style={{ backgroundColor: "#f2f2f2" }}
              >
                <motion.div
                  className="relative h-64"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={testAutoexigenciaImage}
                    alt="Hombre estresado en el trabajo con múltiples tareas - Test de Autoexigencia"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                <CardContent className="p-6">
                  <motion.h3
                    className="text-xl font-semibold mb-4"
                    style={{ color: "#314037" }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    Test de autoexigencia
                  </motion.h3>

                  {/* Test Info */}
                  <motion.div
                    className="flex items-center justify-center gap-6 mb-4 rounded-lg py-3"
                    style={{ backgroundColor: "#d7ceca" }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="flex items-center gap-1" style={{ color: "#68736e" }}>
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">4 min</span>
                    </div>
                    <div className="flex items-center gap-1" style={{ color: "#68736e" }}>
                      <HelpCircle className="w-4 h-4" />
                      <span className="text-sm">15 preguntas</span>
                    </div>
                  </motion.div>

                  <motion.p
                    className="text-sm mb-6 leading-relaxed"
                    style={{ color: "#68736e" }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    ¿Sientes que siempre tienes que hacerlo todo perfecto? ¿Te cuesta delegar o
                    aceptar críticas? Este test te ayudará a identificar cómo la autoexigencia
                    afecta tu vida diaria, desde la planificación hasta las relaciones personales.
                    Descubre tu nivel de autoexigencia para empezar a gestionarla mejor.
                  </motion.p>

                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <Button
                      className="w-full text-white py-3 border-0 hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: "#314037" }}
                      onClick={() => window.open("https://form.typeform.com/to/cSaywX3m", "_blank")}
                    >
                      TEST DE AUTOEXIGENCIA
                    </Button>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Test 3 - Autoestima */}
            <motion.div variants={fadeInRight}>
              <Card
                className="overflow-hidden shadow-lg border-0 h-full"
                style={{ backgroundColor: "#f2f2f2" }}
              >
                <motion.div
                  className="relative h-64"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={testHabitosImage}
                    alt="Hombre tomando decisiones alimentarias en la cocina - Test de Hábitos Alimentarios"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                <CardContent className="p-6">
                  <motion.h3
                    className="text-xl font-semibold mb-4"
                    style={{ color: "#314037" }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    Test de hábitos alimenticios
                  </motion.h3>

                  {/* Test Info */}
                  <motion.div
                    className="flex items-center justify-center gap-6 mb-4 rounded-lg py-3"
                    style={{ backgroundColor: "#d7ceca" }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="flex items-center gap-1" style={{ color: "#68736e" }}>
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">5 min</span>
                    </div>
                    <div className="flex items-center gap-1" style={{ color: "#68736e" }}>
                      <HelpCircle className="w-4 h-4" />
                      <span className="text-sm">15 preguntas</span>
                    </div>
                  </motion.div>

                  <motion.p
                    className="text-sm mb-4 leading-relaxed"
                    style={{ color: "#68736e" }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    ¿Sabes si lo que comes realmente te está ayudando a sentirte bien o, sin darte
                    cuenta, te está quitando energía y afectando tu salud?
                  </motion.p>

                  <motion.p
                    className="text-sm mb-6 leading-relaxed"
                    style={{ color: "#68736e" }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    Este test te va a mostrar con claridad si tus hábitos alimentarios te están
                    impulsando hacia tus objetivos o, al contrario, te están frenando.
                  </motion.p>

                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <Button
                      className="w-full text-white py-3 border-0 hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: "#314037" }}
                      onClick={() => window.open("https://form.typeform.com/to/REV35s5M", "_blank")}
                    >
                      TEST DE HÁBITOS ALIMENTICIOS
                    </Button>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Test 4 - Nuevo Test */}
            <motion.div variants={fadeInLeft}>
              <Card
                className="overflow-hidden shadow-lg border-0 h-full"
                style={{ backgroundColor: "#f2f2f2" }}
              >
                <motion.div
                  className="relative h-64"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={testEntrenamientoImage}
                    alt="Mujer entrenando con determinación - Test de Entrenamiento"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                <CardContent className="p-6">
                  <motion.h3
                    className="text-xl font-semibold mb-4"
                    style={{ color: "#314037" }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    Test de entrenamiento deportivo
                  </motion.h3>

                  {/* Test Info */}
                  <motion.div
                    className="flex items-center justify-center gap-6 mb-4 rounded-lg py-3"
                    style={{ backgroundColor: "#d7ceca" }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="flex items-center gap-1" style={{ color: "#68736e" }}>
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">4 min</span>
                    </div>
                    <div className="flex items-center gap-1" style={{ color: "#68736e" }}>
                      <HelpCircle className="w-4 h-4" />
                      <span className="text-sm">15 preguntas</span>
                    </div>
                  </motion.div>

                  <motion.p
                    className="text-sm mb-4 leading-relaxed"
                    style={{ color: "#68736e" }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    ¿Estás aprovechando al máximo tu tiempo en el gimnasio o en tus rutinas de
                    ejercicio? Este test te permitirá evaluar la efectividad de tu entrenamiento,
                    asegurándote de que cada esfuerzo está orientado a mejorar tanto tu salud como
                    tu físico.
                  </motion.p>

                  <motion.p
                    className="text-sm mb-6 leading-relaxed"
                    style={{ color: "#68736e" }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    Aprende a optimizar tus rutinas y a maximizar los resultados.
                  </motion.p>

                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <Button
                      className="w-full text-white py-3 border-0 hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: "#314037" }}
                      onClick={() => window.open("https://form.typeform.com/to/LJp3fhwm", "_blank")}
                    >
                      TEST DE ENTRENAMIENTO
                    </Button>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.section>

          {/* Products Section */}
          <motion.section
            className="mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <motion.h2
              className="text-3xl font-semibold text-center mb-8"
              style={{ color: "#314037" }}
              variants={bounceIn}
            >
              Descubre nuestros servicios
            </motion.h2>
            <motion.div variants={scaleIn}>
              <ProductCarousel />
            </motion.div>
          </motion.section>
        </main>

        {/* Contact Section */}
        <motion.section
          className="py-8 px-6"
          style={{ backgroundColor: "#bad0a2" }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          variants={fadeInUp}
        >
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
            <motion.div className="flex items-center gap-4" variants={fadeInLeft}>
              <motion.h3 className="text-xl font-semibold text-white" whileHover={{ scale: 1.05 }}>
                ¿ALGUNA DUDA?
              </motion.h3>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  className="bg-white hover:bg-gray-100 border-0 px-6 py-2"
                  style={{ color: "#314037" }}
                  onClick={() => window.open("https://vikika.es/contacto", "_blank")}
                >
                  MÁNDANOS UN MENSAJE
                </Button>
              </motion.div>
            </motion.div>
            <motion.div className="flex items-center gap-4" variants={fadeInRight}>
              <motion.span className="text-white font-medium" whileHover={{ scale: 1.05 }}>
                ÚNETE A NUESTRA COMUNIDAD
              </motion.span>
              <motion.div className="flex gap-3" variants={staggerContainer}>
                <motion.div variants={bounceIn}>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-white hover:bg-white/20"
                    onClick={() => window.open("https://www.facebook.com/vikikateam", "_blank")}
                  >
                    <Facebook className="w-5 h-5" />
                  </Button>
                </motion.div>
                <motion.div variants={bounceIn}>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-white hover:bg-white/20"
                    onClick={() => window.open("https://www.instagram.com/vikikateam/", "_blank")}
                  >
                    <Instagram className="w-5 h-5" />
                  </Button>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </root.div>
  )
}
